import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {isAuthRedirectError, requireAuth} from "../../shared/services/auth.service";
import {logError} from "../../shared/services/logger.service";
import {useToolbar} from "../../components/ToolbarContext";
import {AppointmentDto, AppointmentStatus, AppointmentStatusType, appointmentStatusLabel} from "./dto/appointment.dto";
import {getAppointments} from "./appointment.service";
import AppointmentPage from "./record/AppointmentPage";
import ClientProfilePage from "../client/profile/ClientProfilePage";
import {ListWithRecord, useRecordRoute} from "../../shared/components/record/RecordKit";
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {
  AddButton,
  CardSpec,
  ChipDef,
  ListRow,
  MapListPage,
  StatusListPage,
  useStatusList,
} from "../../shared/components/list-page/ListPageKit";
import RecordsMap from "../../shared/components/list-page/RecordsMap";
import {addressOneLine, displayPhone, formatApptTime} from "../../shared/components/list-page/list-format";

export interface Filter<T> {
  displayName: string;
  urlParam: string;
  filterFunction: (values: T[]) => T[];
}

// Android's chips. Draft, Active and PendingReschedule all read as New.
// Completed appointments are not listed at all (SQ-502): starting the
// estimate completes the appointment, and from then on the estimate is the
// record. The chip says "Canceled" like the other boards; the pill says
// "Cancelled" like the API.
const APPOINTMENT_CHIPS: ChipDef[] = [
  {label: 'New', urlParam: 'new'},
  {label: 'Canceled', urlParam: 'cancelled'},
];

const chipFor = (status: AppointmentStatusType): string =>
  status === AppointmentStatus.Cancelled ? 'Canceled' : 'New';

/*
 * The card's realistic widths, from the Shippy's Tree Service snapshot (122
 * appointments that are not Completed, measured in Chrome with the card's
 * own CSS; 98th percentile, so the longest 2% wrap inside their slot):
 *   name     211px at 18px  "Kendall & Loretta Hopkins"
 *   phone     99px at 14px  "(208) 631-0182"
 *   address  267px at 14px  "181 NE Victor Gust Dr, Mountain Home, ID"
 *   date     259px at 14px  "Appt date/time Wed, Sep 24 at 10:30 AM", the
 *                            widest the format writes
 *   status    75px          the "Cancelled" pill
 * Switch points (cardSwitchPoints): one row from 1,065px, grouped from 659px.
 */
const CARD_SPEC: CardSpec = {
  title: 211,
  status: 75,
  slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 267},
    {key: 'time', group: 'details', width: 259},
  ],
};

const APPOINTMENT_NOUN = {one: 'appointment', many: 'appointments'};

/**
 * The Estimate Appointments page, as on Android: Add New Appointment, the
 * New / Canceled chips + search, one card per appointment, and the map behind
 * the MAP tab. It always opens on the list; the one exception is the old map
 * address, /appointments/map, which redirects here with `mapOpen` in the
 * navigation state. An appointment opens as its own page (/appointments/12,
 * SQ-595) over the list (SQ-574 rule 2). Add New Appointment
 * (/appointments/new) is the client page, blank, in its "adding an
 * appointment" mode (SQ-666, as Android's): find an existing client or type
 * a new one, then create; the appointment opens ready to edit.
 */
function AppointmentListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams()
  const record = useRecordRoute('/appointments');
  const openRecord = record.open;
  const toolbarContext = useToolbar();
  const {hasPermission} = usePermissions();

  const [appointments, setAppointments] = useState<AppointmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const arrivedForMap = (location.state as {mapOpen?: boolean} | null)?.mapOpen === true;
  const [mapOpen, setMapOpen] = useState(arrivedForMap);

  useEffect(() => {
    toolbarContext.initToolbar('AppointmentListPage', '');
    // Nothing is remembered: a reload of this entry opens on the list again.
    if (arrivedForMap) navigate(location.pathname + location.search, {replace: true, state: null});
    void fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchAppointments() {
    setLoading(true);
    setError(null);
    try {
      await requireAuth(navigate);
      const all = await getAppointments(navigate);
      setAppointments(all.filter(appointment => appointment.status !== AppointmentStatus.Completed));
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error fetching appointments', {}, e as Error);
      setError(`Couldn't load appointments: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  const rows: ListRow[] = useMemo(() => appointments.map(appointment => {
    const time = formatApptTime(appointment.time);
    return {
      key: appointment.id,
      title: appointment.clientName?.trim() || `Appointment #${appointment.id}`,
      values: {
        phone: displayPhone(appointment.clientPhoneNumber),
        address: addressOneLine(appointment.estimateAddress, appointment.estimateCity, appointment.estimateState),
        time: time
          ? {label: 'Appt date/time', value: time}
          : {label: 'Appt date/time', value: 'Not scheduled', muted: true},
      },
      status: appointmentStatusLabel(appointment.status),
      chip: chipFor(appointment.status),
      onClick: () => openRecord(appointment.id),
      location: {lat: appointment.estimateLat, lng: appointment.estimateLng},
      mapLine: time ?? 'Not scheduled',
    };
  }), [appointments, openRecord]);

  const list = useStatusList(rows, APPOINTMENT_CHIPS);

  // Links from before records had pages: /appointments?appointmentId=12.
  const legacyId = searchParams.get("appointmentId");
  if (legacyId) return <Navigate to={`/appointments/${legacyId.toLowerCase()}`} replace/>;

  return (
    <ListWithRecord
      list={<MapListPage
        mapOpen={mapOpen}
        onMapOpenChange={setMapOpen}
        list={(
          <StatusListPage
            title="Estimate Appointments"
            titleButton={hasPermission(PermissionCode.AppointmentsCreate)
              ? <AddButton label="Add New Appointment" onClick={() => record.open('NEW')}/>
              : undefined}
            searchLabel="Appointment Filters and Search"
            list={list}
            spec={CARD_SPEC}
            loading={loading}
            error={error}
            onRetry={() => void fetchAppointments()}
          />
        )}
        map={(
          <RecordsMap
            list={list}
            filterLabel="Appointment Filters"
            noun={APPOINTMENT_NOUN}
            loading={loading}
            error={error}
            onRetry={() => void fetchAppointments()}
          />
        )}
      />}
      record={record.id === 'NEW' ? (
        <ClientProfilePage key="new" addingAppointment onClose={record.close} onChanged={() => void fetchAppointments()}/>
      ) : record.id !== undefined && (
        <AppointmentPage key={record.id} appointmentId={record.id} onChanged={() => void fetchAppointments()}/>
      )}
    />
  );
}

export default AppointmentListPage;
